import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from '@heroui/modal';
import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { Input } from '@heroui/input';
import { FormEvent, useCallback, useState } from 'react';
import { Select, SelectItem } from '@heroui/select';
import { useNavigate } from 'react-router-dom';
import { TbTrashFilled } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';

import { SongbookColor, Songbook } from '@/core/db.ts';
import { useEditSongbook, useRemoveSongbook } from '@/hooks/useSongbook.ts';

import ColorIcon from './ColorIcon.tsx';

const colors: SongbookColor[] = ['default', 'primary', 'secondary', 'success', 'danger', 'warning'];

interface SongbookEditModalProps {
  disclosure: ReturnType<typeof useDisclosure>;
  songbook: Songbook;
}

function SongbookEditModal({ disclosure, songbook }: SongbookEditModalProps) {
  const { t } = useTranslation();
  const { isOpen, onOpenChange, onClose } = disclosure;
  const [color, setColorValue] = useState(songbook.color);
  const { mutateAsync: editSongbook, isPending } = useEditSongbook(songbook.id);
  const { mutateAsync: removeSongbook } = useRemoveSongbook(songbook.id);
  const navigate = useNavigate();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    const data = new FormData(e.target as HTMLFormElement);

    await editSongbook({ title: data.get('title')?.toString() || '', color });
    onClose();
  }, [color, onClose, editSongbook]);

  const handleRemove = useCallback(async () => {
    await removeSongbook();
    navigate('/songbook');
  }, [removeSongbook, navigate, onClose]);

  return (
    <Modal backdrop="blur" isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContent className="bg-default-50 text-foreground">
        {(onClose) => isRemoving ? (
          <div className="w-full">
            <ModalHeader className="flex flex-col gap-1">{t('songbook.modal.removeTitle')}</ModalHeader>
            <ModalBody>
              {t('songbook.modal.removeMessage', { title: songbook.title })}
            </ModalBody>
            <ModalFooter>
              <Button color="default" disabled={isPending} onPress={() => setIsRemoving(false)}>
                {t('songbook.modal.cancel')}
              </Button>
              <Button className="text-foreground" color="danger" disabled={isPending} onPress={handleRemove}>
                {t('songbook.modal.removeConfirm')}
              </Button>
            </ModalFooter>
          </div>
          ) : (
          <Form onSubmit={handleSubmit}>
            <div className="w-full">
              <ModalHeader className="flex flex-col gap-1">{t('songbook.modal.editTitle')}</ModalHeader>
              <ModalBody>
                <Input
                  isRequired
                  defaultValue={songbook.title}
                  disabled={isPending}
                  errorMessage={t('songbook.modal.titleError')}
                  label={t('songbook.modal.titleLabel')}
                  labelPlacement="outside"
                  name="title"
                  placeholder={t('songbook.modal.titlePlaceholder')}
                  type="text"
                />
                <Select
                  isRequired
                  color="default"
                  disabled={isPending}
                  label={t('songbook.modal.colorLabel')}
                  labelPlacement="outside"
                  maxListboxHeight={400}
                  placeholder={t('songbook.modal.colorPlaceholder')}
                  selectedKeys={[color]}
                  selectionMode="single"
                  startContent={<ColorIcon c={color} p={0}/>}
                  variant="bordered"
                  onChange={(e) => setColorValue(e.target.value as SongbookColor)}
                >
                  {colors.map((c) => (
                    <SelectItem
                      key={c}
                      className="text-foreground"
                      startContent={<ColorIcon c={c} s={5}/>}
                      textValue=" "
                    />
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter>
                <div className="grow">
                  <Button
                    className="text-foreground"
                    color="danger"
                    disabled={isPending}
                    startContent={<TbTrashFilled />}
                    onPress={() => setIsRemoving(true)}
                  >
                    {t('songbook.modal.remove')}
                  </Button>
                </div>
                <Button color="default" disabled={isPending} variant="light" onPress={onClose}>
                  {t('songbook.modal.cancel')}
                </Button>
                <Button color="primary" disabled={isPending} type="submit">
                  {t('songbook.modal.update')}
                </Button>
              </ModalFooter>
            </div>
          </Form>
        )}
      </ModalContent>
    </Modal>
  );
}

export default SongbookEditModal;
